import React, { useEffect, useRef, useState } from 'react';
import Alert from '../../../common/components/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import HtmlParser, { convertNodeToElement } from 'react-html-parser';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Typography from '@material-ui/core/Typography';
import useFocus from '../../hooks/useFocus';
import { AddRaterForm } from './AddRaterForm';
import { FullPageLoader } from '../../../common/components';
import { LayoutStyles as HideFooter } from '../../styles/common';
import { QuestionTheme } from '../../styles/mfsTheme';
import { Rater } from '../../interfaces/Rater';
import { RaterPageContainer } from './RaterPageContainer';
import { RaterStyles } from '../../styles/rater';
import { RatingQuestionStyles } from '../../styles/ratingQuestion';
import { routeMappings } from '../../../constants';
import { updateRater } from '../../utils/rater';
import { UpdateRaterMode } from '../../constants/updateRaterMode';
import { useHistory, useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useRaterNominationDetails } from '../../store/mfsStoreApi';
import { useTranslation } from 'react-i18next';
import { validateDropdownField, validateTextField } from '../../../authentication/utils/validation';
import { Link } from '@material-ui/core';
import { IELink } from '../../../common/components/IELink';
import { DomElement } from 'htmlparser2';
import { sendPageViewDetails } from '../../../common/utils/gaEvents';
import { GARouteMappings, GAPageViewMappings } from '../../../constants/gaMappings';
import { SearchRaterForm } from './SearchRaterForm';

export const AddRaterPage = (): JSX.Element => {
  const classes = RaterStyles();
  const clsRQ = RatingQuestionStyles(QuestionTheme);
  const customClass = HideFooter();
  const { t } = useTranslation();
  const history = useHistory();
  const routeParams = useParams<{ roleId?: string }>();
  const isSearch = window.location.hash.includes(routeMappings['SEARCH_RATER']);
  const mode = isSearch ? 'search' : 'add';
  const pageHeadingRef = useRef<HTMLHeadingElement>(null);

  const { details, ratingId, setNewAddedRaterId } = useRaterNominationDetails();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorKey, setErrorKey] = useState<string>('');
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  /** Page View GA method */
  useEffect(() => {
    const pathName = isSearch ? GARouteMappings.SHL360_SEARCH : GARouteMappings.SHL360_ADD_RATER;
    const pageName = isSearch ? GAPageViewMappings.SHL360_SEARCH : GAPageViewMappings.SHL360_ADD_RATER;
    sendPageViewDetails(pathName, pageName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rater, setRater] = useState<Rater>({
    raterId: -1,
    ratingId: -1,
    firstName: '',
    firstNameHelperText: '',
    lastName: '',
    lastNameHelperText: '',
    email: '',
    emailHelperText: '',
    language: '',
    languageHelperText: '',
    dept: '',
    country: '',
    gender: '',
    region: '',
    level: '',
    pID: '',
  });

  useFocus(pageHeadingRef);

  useEffect(() => {
    let isDisabled = true;
    if (isSearch) {
      isDisabled = !(
        rater.firstName !== '' ||
        rater.lastName !== '' ||
        rater.email !== '' ||
        rater.region !== '' ||
        rater.country !== '' ||
        rater.level !== '' ||
        rater.dept !== '' ||
        rater.pID !== ''
      );
    } else {
      isDisabled =
        rater.firstName === '' ||
        rater.lastName === '' ||
        rater.email === '' ||
        rater.emailHelperText !== '' ||
        rater.language === '' ||
        rater.language === '-1';
    }
    setButtonDisabled(isDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rater]);

  const addRater = async (): Promise<void> => {
    const responseErrorKey = await updateRater(UpdateRaterMode.ADD, rater, routeParams.roleId);
    setErrorKey(responseErrorKey);
    setLoading(false);

    // Navigate back to rater nomination landing page on success
    if (responseErrorKey === '') {
      setNewAddedRaterId(mode === 'add' ? rater.email : rater.ratingId.toString());
      history.push(`${routeMappings.MFS}${routeMappings.RATERS}/${ratingId}`);
    }
  };

  const onAddClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    if (mode === 'add') {
      if (!loading) {
        setLoading(true);
        addRater();
      }
    } else {
      history.push({
        pathname: `${routeMappings.MFS}${routeMappings.SEARCH_RESULTS}/${routeParams.roleId}`,
        state: {
          firstName: rater.firstName,
          lastName: rater.lastName,
          email: rater.email,
          personalNumber: rater.pID,
          country: rater.country,
          region: rater.region,
          function: rater.dept,
          level: rater.level,
        },
      });
    }
  };

  const onBackClick = (): void => {
    let route = `${routeMappings.MFS}${routeMappings.RATERS}/${ratingId}`;
    if (mode === 'add') {
      route = `${routeMappings.MFS}${routeMappings.SEARCH_RATER}/${routeParams.roleId}`;
    }
    history.push(route);
  };

  const redirectToSearchRater = (): void => {
    const route = `${routeMappings.MFS}${routeMappings.SEARCH_RATER}/${routeParams.roleId}`;
    history.push(route);
  };

  const validateInput = (
    name: keyof Rater,
    value: string,
  ): {
    [name: string]: string | number;
  } => {
    let validation: { [name: string]: string | number } = {};
    switch (name) {
      case 'firstName':
      case 'lastName':
      case 'email':
      case 'pID':
      case 'level':
      case 'dept':
      case 'region':
      case 'country':
        validation = validateTextField(name, value);
        break;
      case 'language':
        validation = validateDropdownField(name, value);
    }
    return validation;
  };

  /** input field change/blur Event handler */
  const inputChangeHandler = (name: keyof Rater) => (
    event?: React.ChangeEvent<{ value: string }>,
    value?: string,
  ): void => {
    if (event) {
      event.preventDefault();
      const value = event.target.value;
      const tempVal = validateInput(name, value);

      setRater({ ...rater, ...tempVal });
    } else if (value) {
      const tempVal = validateInput(name, value);

      setRater({ ...rater, ...tempVal });
    }
  };

  return (
    <>
      <FullPageLoader isLoading={loading} customClass={customClass.screenContainer} />

      <form onSubmit={onAddClick} aria-label={t(isSearch ? 'searchFeedbackProviders' : 'addNewFeedbackProvider')}>
        <RaterPageContainer spacing={3}>
          <Grid xs={12} item>
            <Box mb={1} mt={6} textAlign="center">
              <Typography variant="h2" component="h2" className={classes.pageTitle} ref={pageHeadingRef} tabIndex={-1}>
                {t(isSearch ? 'searchFeedbackProviders' : 'addNewFeedbackProvider')}
              </Typography>
            </Box>
          </Grid>
          <Grid xs={12} item>
            <Box mt={3} mb={errorKey !== '' ? 0 : 6}>
              {isSearch ? (
                <SearchRaterForm
                  rater={rater}
                  languages={!isSearch ? details.languages : undefined}
                  onInputChange={inputChangeHandler}
                />
              ) : (
                <AddRaterForm
                  rater={rater}
                  isSearch={isSearch}
                  languages={!isSearch ? details.languages : undefined}
                  onInputChange={inputChangeHandler}
                />
              )}
            </Box>
          </Grid>

          {/** alert message in case api fails */}
          <Alert show={errorKey !== ''} state="error">
            {HtmlParser(t(errorKey), {
              transform: (node: DomElement): JSX.Element | undefined => {
                if (node.type === 'tag' && node.name === 'a' && errorKey === 'addNewRaterExisitValidation') {
                  return convertNodeToElement(node, 1, () => (
                    <span
                      className={classes.noSearchResultsLink}
                      aria-label={node.children && node.children[0].data}
                      role="link"
                      onClick={redirectToSearchRater}>
                      {node.children && node.children[0].data}
                    </span>
                  ));
                }
              },
            })}
          </Alert>

          <Grid xs={12} item>
            <Box display="flex">
              <Grid xs item className={classes.textAlignLeft}>
                <Button
                  variant="contained"
                  className={clsx(classes.doneButton, classes.width100)}
                  onClick={onBackClick}
                  aria-label={t('back')}>
                  <NavigateBeforeIcon className={clsRQ.backArrowIcon} /> {t('back')}
                </Button>
              </Grid>
              <Grid xs item className={classes.textAlignRight}>
                <Button
                  variant="contained"
                  type="submit"
                  className={clsx(classes.doneButton, mode === 'search' ? classes.width118 : classes.width100)}
                  disabled={buttonDisabled}
                  onClick={onAddClick}
                  aria-label={t(mode)}>
                  {t(mode)} <NavigateNextIcon className={clsRQ.nextArrowIcon} />
                </Button>
              </Grid>
            </Box>
          </Grid>
          {!details.page.disableAddNewRater && mode !== 'add' && (
            <Link
              className={classes.addFeedbackProviderLink}
              component={RouterLink}
              to={`${routeMappings.MFS}${routeMappings.ADD_NEW_RATER}/${routeParams.roleId}`}>
              <IELink>{t('addNewFeedbackProvider')}</IELink>
            </Link>
          )}
        </RaterPageContainer>
      </form>
    </>
  );
};
